import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download, Loader2, SlidersHorizontal, Sparkles, X } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import {
  ERROR_CODES, OUTCOMES, PERIODS, USAGE_MODELS, actionSummary, activeFilterCount, customRangeError, dateTimeOf, formatCost, formatDuration,
  formatPercent, formatTokens, monthGauge, periodRange, toolLabels, usageParams,
} from "../lib/assistantUsage";
import { LoadError, SectionHeader } from "./Settings";
import UsageTurnSheet, { OutcomeBadge } from "../components/copilot/UsageTurnSheet";
import { TopTools, UsageCharts } from "../components/copilot/UsageCharts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ResponsivePopover, ResponsivePopoverContent, ResponsivePopoverTrigger } from "../components/ui/responsive-popover";
import {
  ResponsiveDropdownMenu, ResponsiveDropdownMenuContent, ResponsiveDropdownMenuItem, ResponsiveDropdownMenuTrigger,
} from "../components/ui/responsive-dropdown-menu";

const ALL = "__all__";

// Paramètres › Consommation du Copilote — `assistant:view_usage` (delegable) or the administrator. Figures, estimated costs and
// tools, never the text of the exchanges (only the administrator can open it, question by question, from the detail drawer).
export default function SettingsAssistantUsage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [period, setPeriod] = useState("30d");
  const [custom, setCustom] = useState(() => periodRange("30d"));
  const [filters, setFilters] = useState({});
  const [summary, setSummary] = useState(null);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [turns, setTurns] = useState(null);
  const [page, setPage] = useState(1);
  const [openTurn, setOpenTurn] = useState(null);
  const [people, setPeople] = useState([]);
  const [toolOptions, setToolOptions] = useState([]);
  const seq = useRef(0);

  const rangeError = period === "custom" ? customRangeError(custom) : null;
  const range = useMemo(() => periodRange(period, new Date(), custom), [period, custom]);
  const params = useMemo(() => usageParams(range, filters), [range, filters]);

  const loadSummary = useCallback(async () => {
    if (rangeError) return;
    const mine = (seq.current += 1);
    setSummaryFailed(false);
    try {
      const { data } = await api.get("/assistant/usage/summary", { params });
      if (mine !== seq.current) return;  // a newer filter answered first: never let an old one overwrite it
      setSummary(data);
      // Options of the person / tool filters: everyone and everything seen so far in this page.
      setPeople((list) => mergeOptions(list, data.users.map((u) => ({ value: u.user_id, label: u.name }))));
      setToolOptions((list) => mergeOptions(list, data.tools.map((t) => ({ value: t.tool, label: t.label }))));
    } catch (e) {
      if (mine === seq.current) setSummaryFailed(true);
    }
  }, [params, rangeError]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { setPage(1); }, [params]);

  useEffect(() => {
    if (rangeError) return undefined;
    let cancelled = false;
    setTurns(null);
    api.get("/assistant/usage/turns", { params: { ...params, page } })
      .then(({ data }) => { if (!cancelled) setTurns(data); })
      .catch(() => { if (!cancelled) setTurns({ items: [], total: 0, failed: true, page_size: 25 }); });
    return () => { cancelled = true; };
  }, [params, page, rangeError]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value === ALL ? undefined : value }));

  return (
    <div className="space-y-4" data-testid="settings-assistant-usage">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <SectionHeader
          title="Consommation du Copilote"
          description="Questions, tokens et coût estimé du Copilote Jamora. La facture de la console Anthropic fait foi."
        />
        <ExportMenu params={params} disabled={!!rangeError} />
      </div>

      <FilterBar period={period} setPeriod={setPeriod} custom={custom} setCustom={setCustom} rangeError={rangeError}
        filters={filters} setFilter={setFilter} clear={() => setFilters({})} people={people} tools={toolOptions} />

      {summaryFailed ? <LoadError onRetry={loadSummary} /> : summary == null ? <SummarySkeleton /> : (
        <>
          <MonthGauge month={summary.month} />
          <Totals totals={summary.totals} />
          {summary.totals.questions === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center" data-testid="usage-empty">
              <Sparkles className="mx-auto text-slate-300" size={22} />
              <p className="mt-2 font-medium text-slate-800">Aucune question sur cette période</p>
              <p className="mt-1 text-sm text-slate-500">Élargissez la période ou retirez un filtre.</p>
            </div>
          ) : (
            <>
              <UsageCharts days={summary.days} />
              <UsersTable users={summary.users} isAdmin={isAdmin} onChanged={loadSummary} />
              <TopTools tools={summary.tools} />
            </>
          )}
        </>
      )}

      <TurnsTable turns={turns} page={page} setPage={setPage} onOpen={setOpenTurn} />
      <UsageTurnSheet turnId={openTurn} isAdmin={isAdmin} currentUserId={user?.id} onClose={() => setOpenTurn(null)} />
    </div>
  );
}

function mergeOptions(list, more) {
  const byValue = new Map(list.map((o) => [o.value, o]));
  for (const option of more) byValue.set(option.value, option);
  return [...byValue.values()].sort((a, b) => a.label.localeCompare(b.label, "fr"));
}

// --- filters ----------------------------------------------------------------------------------------------------------
function FilterBar({ period, setPeriod, custom, setCustom, rangeError, filters, setFilter, clear, people, tools }) {
  const count = activeFilterCount(filters);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="h-9 w-[11.5rem]" aria-label="Période" data-testid="usage-period"><SelectValue /></SelectTrigger>
          <SelectContent>{PERIODS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
        </Select>
        {period === "custom" && (
          <div className="flex items-center gap-2">
            <Input type="date" className="h-9 w-[9.5rem]" aria-label="Du" value={custom.from} max={custom.to}
              onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} data-testid="usage-from" />
            <span className="text-sm text-slate-400">au</span>
            <Input type="date" className="h-9 w-[9.5rem]" aria-label="Au" value={custom.to} min={custom.from}
              onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} data-testid="usage-to" />
          </div>
        )}
        <ResponsivePopover>
          <ResponsivePopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-9" data-testid="usage-filters">
              <SlidersHorizontal size={14} className="mr-1.5" /> Filtres
              {count > 0 && <span className="ml-1.5 rounded-full bg-jamora px-1.5 text-2xs text-white">{count}</span>}
            </Button>
          </ResponsivePopoverTrigger>
          <ResponsivePopoverContent align="start" className="w-72 space-y-3">
            <FilterSelect label="Utilisateur" value={filters.user_id} onChange={(v) => setFilter("user_id", v)} options={people}
              all="Tous les utilisateurs" testId="usage-filter-user" />
            <FilterSelect label="Modèle" value={filters.model} onChange={(v) => setFilter("model", v)} options={USAGE_MODELS}
              all="Tous les modèles" testId="usage-filter-model" />
            <FilterSelect label="Issue" value={filters.outcome} onChange={(v) => setFilter("outcome", v)}
              options={Object.entries(OUTCOMES).map(([value, o]) => ({ value, label: o.label }))} all="Toutes les issues"
              testId="usage-filter-outcome" />
            <FilterSelect label="Outil" value={filters.tool} onChange={(v) => setFilter("tool", v)} options={tools} all="Tous les outils"
              testId="usage-filter-tool" />
          </ResponsivePopoverContent>
        </ResponsivePopover>
        {count > 0 && <Button variant="ghost" size="sm" className="h-9" onClick={clear}>Effacer les filtres</Button>}
      </div>
      {rangeError && <p className="text-sm text-amber-800" role="alert">{rangeError}</p>}
      {count > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="usage-chips">
          {[["user_id", people], ["model", USAGE_MODELS], ["outcome", Object.entries(OUTCOMES).map(([value, o]) => ({ value, label: o.label }))], ["tool", tools]]
            .filter(([key]) => filters[key]).map(([key, options]) => (
              <span key={key} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white py-0.5 pl-2.5 pr-1 text-xs text-slate-700">
                {options.find((o) => o.value === filters[key])?.label || filters[key]}
                <button type="button" className="rounded-full p-0.5 hover:bg-slate-100" aria-label="Retirer ce filtre" onClick={() => setFilter(key, ALL)}>
                  <X size={12} />
                </button>
              </span>
            ))}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, all, testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Select value={value || ALL} onValueChange={onChange}>
        <SelectTrigger className="h-9" data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>{all}</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function ExportMenu({ params, disabled }) {
  const [busy, setBusy] = useState(false);
  const download = async (kind) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/assistant/usage/export/${kind}.csv`, { params, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `copilote-${kind}-${params.from}-${params.to}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error("Impossible de télécharger l'export.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <ResponsiveDropdownMenu>
      <ResponsiveDropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled || busy} data-testid="usage-export">
          {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />} Exporter
        </Button>
      </ResponsiveDropdownMenuTrigger>
      <ResponsiveDropdownMenuContent align="end" title="Exporter (CSV, sans texte)">
        <ResponsiveDropdownMenuItem onClick={() => download("questions")} data-testid="usage-export-questions">Par question</ResponsiveDropdownMenuItem>
        <ResponsiveDropdownMenuItem onClick={() => download("users")} data-testid="usage-export-users">Par utilisateur</ResponsiveDropdownMenuItem>
      </ResponsiveDropdownMenuContent>
    </ResponsiveDropdownMenu>
  );
}

// --- head of the page -------------------------------------------------------------------------------------------------
function SummarySkeleton() {
  return (
    <div className="space-y-4" aria-hidden="true">
      <div className="h-20 animate-pulse rounded-xl bg-slate-100" />
      <div className="h-36 animate-pulse rounded-xl bg-slate-100" />
      <div className="h-56 animate-pulse rounded-xl bg-slate-100" />
    </div>
  );
}

function MonthGauge({ month }) {
  const gauge = monthGauge(month);
  const bar = { ok: "bg-jamora", warning: "bg-amber-500", reached: "bg-red-600" }[gauge.level] || "bg-jamora";
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="usage-month">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-sm font-semibold text-slate-900">Plafond mensuel de l'équipe</div>
        <div className="text-sm tabular-nums text-slate-700">
          <span className="font-semibold text-slate-900">{formatCost(month.spent_nanodollars)}</span>
          {gauge.limited ? ` sur ${formatCost(month.budget_nanodollars)}` : " ce mois-ci · aucun plafond"}
        </div>
      </div>
      {gauge.limited && (
        <>
          <div className="mt-3 h-2 rounded-full bg-slate-100" role="progressbar" aria-label="Part du plafond mensuel consommée"
            aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(gauge.share * 100)}>
            <div className={`h-2 rounded-full ${bar}`} style={{ width: `${Math.max(gauge.share * 100, gauge.share ? 1 : 0)}%` }} />
          </div>
          <p className={`mt-2 text-xs ${gauge.level === "ok" ? "text-slate-500" : gauge.level === "warning" ? "text-amber-800" : "text-red-700"}`}>
            {gauge.level === "reached" ? "Plafond atteint : plus personne ne peut poser de question jusqu'au 1er du mois prochain."
              : month.projected_nanodollars == null ? "Projection disponible après la première heure du mois."
                : `Au rythme actuel : ${formatCost(month.projected_nanodollars)} en fin de mois${gauge.level === "warning" ? ", au-delà du plafond" : ""}.`}
          </p>
        </>
      )}
    </div>
  );
}

function Totals({ totals }) {
  const figures = [
    ["Coût estimé", formatCost(totals.cost_nanodollars), "usage-cost"],
    ["Questions", formatTokens(totals.questions), "usage-questions"],
    ["Conversations", formatTokens(totals.conversations)],
    ["Utilisateurs actifs", formatTokens(totals.users)],
    ["Coût moyen / question", formatCost(totals.cost_per_question_nanodollars)],
    ["Temps de réponse", totals.latency_median_ms == null ? "—" : `${formatDuration(totals.latency_median_ms)} · p95 ${formatDuration(totals.latency_p95_ms)}`],
    ["Taux d'erreur", formatPercent(totals.error_rate)],
    ["Taux de refus", formatPercent(totals.refusal_rate)],
  ];
  const tokens = [
    ["Entrée", totals.input_tokens], ["Cache lu", totals.cache_read_input_tokens], ["Cache écrit", totals.cache_creation_input_tokens],
    ["Sortie", totals.output_tokens],
  ];
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="usage-totals">
      <dl className="grid grid-cols-2 divide-slate-100 sm:grid-cols-4">
        {figures.map(([label, value, testId], i) => (
          <div key={label} className={`px-4 py-3 ${i % 2 ? "border-l" : ""} ${i % 4 ? "sm:border-l" : "sm:border-l-0"} ${i >= 2 ? "border-t" : ""} ${i >= 4 ? "sm:border-t" : "sm:border-t-0"} border-slate-100`}>
            <dt className="text-xs text-slate-500">{label}</dt>
            <dd className="mt-0.5 truncate text-base font-semibold tabular-nums text-slate-900" data-testid={testId}>{value}</dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-slate-100 px-4 py-2.5 text-xs text-slate-500">
        <span className="font-medium text-slate-700">Tokens</span>
        {tokens.map(([label, value]) => <span key={label}>{label} <span className="tabular-nums text-slate-800">{formatTokens(value)}</span></span>)}
        <span>Cache <span className="tabular-nums text-slate-800">{formatPercent(totals.cache_rate)}</span> de l'entrée</span>
      </div>
      {totals.actions?.proposed > 0 && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-slate-100 px-4 py-2.5 text-xs text-slate-500" data-testid="usage-actions">
          <span className="font-medium text-slate-700">Actions</span>
          <span className="tabular-nums text-slate-800">{actionSummary(totals.actions)}</span>
        </div>
      )}
    </div>
  );
}

// --- per person -------------------------------------------------------------------------------------------------------
function UsersTable({ users, isAdmin, onChanged }) {
  const [saving, setSaving] = useState(null);
  const toggle = async (row, enabled) => {
    setSaving(row.user_id);
    try {
      await api.patch(`/assistant/access/${row.user_id}`, { enabled });
      toast.success(enabled ? `${row.name} peut utiliser le Copilote` : `Accès au Copilote suspendu pour ${row.name}`);
      onChanged();
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de modifier l'accès."));
    } finally {
      setSaving(null);
    }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="usage-users">
      <h4 className="px-4 pt-4 text-sm font-semibold text-slate-900">Par utilisateur</h4>
      <div className="hidden md:block overflow-x-auto">
        <table className="mt-2 w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-left text-xs text-slate-500">
              <th className="px-4 py-2 font-semibold">Utilisateur</th>
              <th className="px-2 py-2 text-right font-semibold">Questions</th>
              <th className="px-2 py-2 text-right font-semibold">Entrée / sortie</th>
              <th className="px-2 py-2 text-right font-semibold">Coût estimé</th>
              <th className="px-2 py-2 text-right font-semibold">Plafond du jour</th>
              {isAdmin && <th className="px-4 py-2 text-right font-semibold">Accès</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 tabular-nums">
            {users.map((u) => (
              <tr key={u.user_id} className="h-12">
                <td className="px-4 py-2">
                  <div className="font-medium text-slate-900">{u.name}</div>
                  <div className="text-xs text-slate-500">Dernière question {dateTimeOf(u.last_at)}</div>
                </td>
                <td className="px-2 py-2 text-right">{u.questions}</td>
                <td className="px-2 py-2 text-right text-slate-600">
                  {formatTokens(u.input_tokens + u.cache_read_input_tokens + u.cache_creation_input_tokens, { short: true })} / {formatTokens(u.output_tokens, { short: true })}
                </td>
                <td className="px-2 py-2 text-right font-medium">{formatCost(u.cost_nanodollars)}</td>
                <td className="px-2 py-2 text-right text-slate-600">{u.daily_share == null ? "—" : formatPercent(u.daily_share)}</td>
                {isAdmin && (
                  <td className="px-4 py-2 text-right">
                    {u.enabled == null ? <span className="text-xs text-slate-400">—</span> : (
                      <Switch checked={u.enabled} disabled={saving === u.user_id} onCheckedChange={(v) => toggle(u, v)}
                        aria-label={`Accès de ${u.name} au Copilote`} data-testid={`usage-access-${u.user_id}`} />
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="mt-2 divide-y divide-slate-100 md:hidden">
        {users.map((u) => (
          <li key={u.user_id} className="flex items-center gap-3 px-4 py-3">
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-slate-900">{u.name}</div>
              <div className="text-xs tabular-nums text-slate-500">
                {u.questions} question{u.questions > 1 ? "s" : ""} · {formatCost(u.cost_nanodollars)}
                {u.daily_share != null && ` · ${formatPercent(u.daily_share)} du plafond du jour`}
              </div>
            </div>
            {isAdmin && u.enabled != null && (
              <Switch checked={u.enabled} disabled={saving === u.user_id} onCheckedChange={(v) => toggle(u, v)} aria-label={`Accès de ${u.name} au Copilote`} />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- per question -----------------------------------------------------------------------------------------------------
function TurnsTable({ turns, page, setPage, onOpen }) {
  const pages = turns ? Math.max(1, Math.ceil(turns.total / turns.page_size)) : 1;
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="usage-turns">
      <div className="flex items-baseline justify-between px-4 pt-4">
        <h4 className="text-sm font-semibold text-slate-900">Par question</h4>
        {turns && <span className="text-xs text-slate-500">{turns.total} question{turns.total > 1 ? "s" : ""}</span>}
      </div>
      {turns == null && <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />)}</div>}
      {turns?.failed && <p className="px-4 py-6 text-sm text-red-700">Impossible de charger les questions.</p>}
      {turns && !turns.failed && turns.items.length === 0 && <p className="px-4 py-6 text-sm text-slate-500">Aucune question sur cette période.</p>}
      {turns && turns.items.length > 0 && (
        <>
          <div className="hidden md:block overflow-x-auto">
            <table className="mt-2 w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs text-slate-500">
                  <th className="px-4 py-2 font-semibold">Date (UTC)</th>
                  <th className="px-2 py-2 font-semibold">Utilisateur</th>
                  <th className="px-2 py-2 font-semibold">Outils</th>
                  <th className="px-2 py-2 font-semibold">Actions</th>
                  <th className="px-2 py-2 text-right font-semibold">Tokens</th>
                  <th className="px-2 py-2 text-right font-semibold">Coût</th>
                  <th className="px-2 py-2 text-right font-semibold">Durée</th>
                  <th className="px-4 py-2 font-semibold">Issue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {turns.items.map((t) => (
                  <tr key={t.id} className="h-12 cursor-pointer hover:bg-slate-50" onClick={() => onOpen(t.id)} data-testid={`usage-turn-${t.id}`}>
                    <td className="px-4 py-2 whitespace-nowrap tabular-nums text-slate-700">
                      <button type="button" className="text-left hover:underline" onClick={(e) => { e.stopPropagation(); onOpen(t.id); }}>{dateTimeOf(t.at)}</button>
                    </td>
                    <td className="px-2 py-2 text-slate-900">{t.user_name}</td>
                    <td className="max-w-[12rem] truncate px-2 py-2 text-xs text-slate-500" title={toolLabels(t).join(", ")}>
                      {t.tools.length ? `${t.tools.length} · ${toolLabels(t).join(", ")}` : "—"}
                    </td>
                    <td className="max-w-[10rem] truncate px-2 py-2 text-xs text-slate-500" title={actionSummary(t.actions)} data-testid="usage-turn-actions">
                      {actionSummary(t.actions) || "—"}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-600">
                      {formatTokens(t.input_tokens + t.cache_read_input_tokens + t.cache_creation_input_tokens + t.output_tokens, { short: true })}
                    </td>
                    <td className="px-2 py-2 text-right tabular-nums font-medium">{formatCost(t.cost_nanodollars)}</td>
                    <td className="px-2 py-2 text-right tabular-nums text-slate-600">{t.calls ? formatDuration(t.duration_ms) : "—"}</td>
                    <td className="px-4 py-2"><OutcomeBadge outcome={t.outcome} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="mt-2 divide-y divide-slate-100 md:hidden">
            {turns.items.map((t) => (
              <li key={t.id}>
                <button type="button" className="flex w-full items-start gap-3 px-4 py-3 text-left" onClick={() => onOpen(t.id)}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium text-slate-900">{t.user_name}</span>
                      <OutcomeBadge outcome={t.outcome} />
                    </div>
                    <div className="mt-0.5 text-xs tabular-nums text-slate-500">
                      {dateTimeOf(t.at)} · {formatCost(t.cost_nanodollars)}{t.tools.length ? ` · ${t.tools.length} outil${t.tools.length > 1 ? "s" : ""}` : ""}
                      {t.actions?.proposed ? ` · ${actionSummary(t.actions)}` : ""}
                      {t.error_code && t.outcome !== "success" ? ` · ${ERROR_CODES[t.error_code] || t.error_code}` : ""}
                    </div>
                  </div>
                  <ChevronRight size={16} className="mt-1 shrink-0 text-slate-300" />
                </button>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-2 text-sm" data-testid="usage-turns-pager">
            <span className="text-xs text-slate-500">
              {(page - 1) * turns.page_size + 1}–{Math.min(page * turns.page_size, turns.total)} sur {turns.total} · page {page} sur {pages}
            </span>
            <Button variant="ghost" size="icon" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Page précédente"><ChevronLeft size={16} /></Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" disabled={page >= pages} onClick={() => setPage(page + 1)} aria-label="Page suivante"><ChevronRight size={16} /></Button>
          </div>
        </>
      )}
    </div>
  );
}
